import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { isMgbwWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import { isThemeActive, THEME_IDS } from "@tSurya/theme/themeHelper";
import React from "react";
import { css } from "styled-components";

interface Props {
    products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
    onViewAll?: () => void;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 10px 0 5px 0;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
            background-color: lightgray;
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 65px;
            max-height: 50px;
            flex-basis: 65px;
        `,
    },
    image: {
        width: "50px",
        css: css`
            img {
                height: 100%;
                object-fit: contain;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
            `,
        },
        css: css`
            width: 270px;
        `,
    },
    erpNumberText: {
        ellipsis: true,
        css: css`
            width: 270px;
            text-align: left;
            margin: 5px 0 0 0;
        `,
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products, onViewAll } = this.props;
        const styles = this.styles;
        const isMgbw = isThemeActive(THEME_IDS.MGBW);
        const isDesktop = isMgbw && typeof window !== 'undefined' && window.innerWidth >= 1167;

        if (isMgbw) {
            const mgbwHeaderWrapper: InjectableCss = {
                css: css`
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    width: 100%;
                `,
            };
            const mgbwProductsRow: InjectableCss = {
                css: css`
                    display: flex;
                    gap: 24px;
                    align-items: flex-start;
                    align-self: stretch;
                    width: 100%;
                `,
            };
            const mgbwProductCard: InjectableCss = {
                css: css`
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    align-items: flex-start;
                    width: 180px;
                    flex-shrink: 0;
                    cursor: pointer;
                `,
            };
            return (
                <>
                    <StyledWrapper {...mgbwHeaderWrapper}>
                        <Typography
                            css={css`
                                font-family: ${TYPOGRAPHY.fontFamily.base};
                                font-size: 14px;
                                font-weight: 500;
                                letter-spacing: 1.6px;
                                text-transform: uppercase;
                                line-height: 1.5;
                                color: #333;
                            `}
                        >
                            {translate("Products")}
                        </Typography>
                        <Link
                            onClick={onViewAll}
                            css={css`
                                font-family: ${TYPOGRAPHY.fontFamily.base};
                                font-size: 14px;
                                font-weight: 700;
                                letter-spacing: 1.6px;
                                text-transform: uppercase;
                                line-height: 1.5;
                                color: ${COLORS.text.link} !important;
                                cursor: pointer;
                                text-decoration: none;
                            `}
                        >
                            {translate("View All")}
                        </Link>
                    </StyledWrapper>
                    {isDesktop ? (
                        <StyledWrapper {...mgbwProductsRow}>
                            {products.map(product => (
                                <StyledWrapper key={product.id} {...mgbwProductCard} onClick={() => this.props.goToUrl(product.url)}>
                                    <LazyImage
                                        src={product.image}
                                        css={css`
                                            width: 100%;
                                            height: 174px;
                                            aspect-ratio: 30/29;
                                            align-self: stretch;
                                            overflow: hidden;
                                            img {
                                                width: 100%;
                                                height: 100%;
                                                display: block;
                                                ${isMgbwWebsite()
                                                    ? css`
                                                          object-fit: contain;
                                                      `
                                                    : css`
                                                          object-fit: cover;
                                                      `}
                                            }
                                        `}
                                    />
                                    <Typography
                                        css={css`
                                            font-family: ${TYPOGRAPHY.fontFamily.base};
                                            font-size: 14px;
                                            font-weight: 400;
                                            color: #333;
                                            letter-spacing: 0.08px;
                                            line-height: 1.5;
                                            text-align: center;
                                            align-self: stretch;
                                        `}
                                    >
                                        {product.displayTitle}
                                    </Typography>
                                </StyledWrapper>
                            ))}
                        </StyledWrapper>
                    ) : (
                        products.map(product => (
                            <GridContainer
                                {...(this.props.focusedItem === product ? styles.focusedContainer : styles.container)}
                                key={product.id!}
                                onClick={() => this.props.goToUrl(product.url)}
                                className="autocomplete-products-container"
                            >
                                <GridItem {...styles.imageGridItem} className="autocomplete-products-image-grid-item">
                                    <LazyImage {...styles.image} src={product.image} className="autocomplete-products-image" />
                                </GridItem>
                                <GridItem {...styles.infoGridItem} className="autocomplete-products-info-grid-item">
                                    <Link {...styles.titleLink} data-test-selector={`productId_${product.id}`} className="autocomplete-products-link">
                                        {product.displayTitle}
                                    </Link>
                                </GridItem>
                            </GridContainer>
                        ))
                    )}
                </>
            );
        }

        return (
            <>
                <Typography
                    {...styles.headerText}
                    data-test-selector="autocompleteProducts_Header"
                    className="autocomplete-products-header"
                >
                    {translate("Products")}
                </Typography>
                {products.map(product => (
                    <GridContainer
                        {...(this.props.focusedItem === product ? styles.focusedContainer : styles.container)}
                        key={product.id!}
                        onClick={() => {
                            this.props.goToUrl(product.url);
                        }}
                        className="autocomplete-products-container"
                    >
                        <GridItem {...styles.imageGridItem} className="autocomplete-products-image-grid-item">
                            <LazyImage {...styles.image} src={product.image} className="autocomplete-products-image" />
                        </GridItem>
                        <GridItem {...styles.infoGridItem} className="autocomplete-products-info-grid-item">
                            <Link
                                {...styles.titleLink}
                                data-test-selector={`productId_${product.id}`}
                                className="autocomplete-products-link"
                            >
                                {product.displayTitle}
                            </Link>
                            <Typography
                                {...styles.erpNumberText}
                                data-test-selector="autocompleteProducts_PartNumber"
                                className="autocomplete-products-erp-number"
                            >
                                {product.isNameCustomerOverride ? (
                                    <>
                                        {translate("My Part #")}
                                        {product.displayName}
                                    </>
                                ) : (
                                    <>
                                        {/* {translate("Part #")} */}
                                        {/* {product.erpNumber.includes('_P') ? product.erpNumber.replace('_P', '') : product.erpNumber} */}
                                    </>
                                )}
                                {product.manufacturerItemNumber && (
                                    <>
                                        &nbsp;
                                        {translate("MFG #")}
                                        {product.displayManufacturerItemNumber}
                                    </>
                                )}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                ))}
            </>
        );
    }
}

export default AutocompleteProducts;
